import React, { useContext, useEffect, useState } from 'react';
import Card from '~/components/shared/Card/Card';
import CardHeader from '~/components/shared/CardHeader/CardHeader';
import CardSection from '~/components/shared/CardSection/CardSection';
import useTranslate from '~/hooks/useTranslate';
import { Typography } from '~/components/shared/Typography/Typography';
import { theme } from '~/styles/themes';
import * as offerActions from '~/store/actions/loanOfferActions';
import { useDispatch, useSelector } from 'react-redux';
import Button, { ButtonType } from '~/components/shared/Button/button';
import { RootState } from '~/store/types/sharedTypes';
import { Offer } from '~/Interfaces';
import { FormStatus, ValidationContext } from '~/components/hoc/withValidation';
import SpinnerLinear from '~/components/shared/Spinner/SpinnerLinear';
import DownloadPdf from '~/components/shared/DownloadPdf/DownloadPdf';
import { apiGetOfferPdf } from '~/services/api';
import { generatePdfHelper } from '~/helpers/generatePdf.helper';
import { RegularMailFees } from '~/constants/markets';
import Checkbox from '~/components/shared/CheckBox/CheckBox';
import { CountryCode, OwnerTypeEnum } from '~/enums';
import useModals from '~/hooks/useModals';
import TermsAndConditionsModal from '~/components/shared/Terms/TermsAndConditionsModal';
import { TextHeader } from '~/components/shared/Terms/styledComponents';
import PolicyAgreement from '~/components/shared/PolicyAgreement/PolicyAgreement';
import { ClickableText } from '~/styles/BaseStyledComponents';
import { Signer } from '~/types/types';

interface Props {
  data: Offer;
  // Following props come only from public offer page
  hash: string | null;
  clientId: number | null;
}

const TermsAndConfirmationCard: React.FC<Props> = (props) => {
  /* States */
  const [downloadError, setDownloadError] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  /* Selectors */
  const {
    matchedMedia,
    intl: { market },
  } = useSelector((state: RootState) => state);
  const { userData, isSubmitting } = useSelector(
    (state: RootState) => state.loanOffer
  );
  const { owners: beneficialOwners } = userData;

  /* Other hooks */
  const validationContext = useContext(ValidationContext);
  const translate = useTranslate();
  const dispatch = useDispatch();
  const modals = useModals();

  /* Effect */
  useEffect(() => {
    dispatch(
      offerActions.updateOfferData({
        applicationId: props.data.applicationId,
        clientId: props.data.clientId,
        clientName: props.data.clientName,
        personalNumber:
          market === CountryCode.NL
            ? props.data.dateOfBirth
            : props.data.personalNumber,
      })
    );
  }, []);

  /* Handlers */
  const invoiceMailCheckboxHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(offerActions.setRegularMailForInvoices(e.currentTarget.checked));
  };

  const personalLiabilityCheckboxHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(
      offerActions.setPersonalLiabilityIsAgreed(e.currentTarget.checked)
    );
  };

  const userAgreementCheckboxHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(offerActions.setOfferIsAgreed(e.currentTarget.checked));
  };

  const onDownloadOfferClick = () => {
    if (isDownloading) return;
    setIsDownloading(true);
    apiGetOfferPdf(
      userData.applicationId,
      userData.clientId,
      userData.amount,
      props.data.maxAllowedTerm,
      !!props.hash
    )
      .then((res: any) => {
        generatePdfHelper(res.data, 'loan offer');
        setDownloadError('');
      })
      .catch((err: any) => {
        console.log(err);
        setDownloadError(
          translate(
            'TermsAndConfirmationCard.DownloadContractErrorMessage'
          ) as string
        );
        setIsDownloading(false);
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  const handleTermAndConditionModal = () => {
    modals.openModal({
      header: (
        <TextHeader>
          {
            translate(
              'TermsAndConfirmationCard.UserAgreementPopupHeader'
            ) as string
          }
        </TextHeader>
      ),
      children: <TermsAndConditionsModal />,
      size: 'xl',
      fullScreen: matchedMedia.smallScreenPortrait,
      filledBackground: true,
    });
  };

  const signContract = () => {
    if (isSubmitting) return;

    // TODO: find a way to consolidate FormStatus of validation context with submitting status in redux
    validationContext.setFormStatus(FormStatus.SUBMITTING);

    if (validationContext.isFormValid) {
      dispatch(
        offerActions.sendOfferData(userData, props.hash, market, props.clientId)
      );
    } else {
      validationContext.setFormStatus(FormStatus.SUBMITTED);
    }
  };

  const atLeastOneOwnerHasMoreThan25 =
    OwnerTypeEnum.MorePersonsOwn25 &&
    beneficialOwners.some((o) => Number(o.percentage) >= 25);

  return (
    <Card noPadding dataTestId="loan-offer-terms-and-configuration-card">
      <CardHeader
        value={translate('TermsAndConfirmationCard.Title') as string}
      />
      <CardSection borderTop borderBottom>
        {market === CountryCode.BE &&
          props.data.signers &&
          props.data.signers.length > 0 && (
            <Typography lineHeight={1.7} fontSize={1}>
              {translate(
                'TermsAndConfirmationCard.SigningOrderBelgiumSpecificText'
              )}
              <span>{`1.${props.data.clientName}, `}</span>
              {props.data.signers.map(
                (signer: Signer, index: number, { length }) => (
                  <span>
                    {signer.name}
                    {index !== length - 1 && ', '}
                  </span>
                )
              )}
            </Typography>
          )}
        <Checkbox
          checked={userData.regularMailForInvoices}
          onChange={invoiceMailCheckboxHandler}
        >
          {translate('TermsAndConfirmationCard.RegularMail', {
            fee: RegularMailFees[market],
          })}
        </Checkbox>
      </CardSection>
      <CardSection borderBottom>
        <Checkbox
          checked={userData.personalLiabilityIsAgreed}
          onChange={personalLiabilityCheckboxHandler}
        >
          {translate('TermsAndConfirmationCard.PersonalLiability', {
            lineBreak: (
              <>
                <br />
                <br />
              </>
            ),
          })}
        </Checkbox>
      </CardSection>
      <CardSection borderBottom>
        <Checkbox
          checked={userData.offerIsAgreed}
          onChange={userAgreementCheckboxHandler}
        >
          {translate('TermsAndConfirmationCard.UserAgreementText')}{' '}
          <ClickableText onClick={handleTermAndConditionModal}>
            {translate('TermsAndConfirmationCard.UserAgreementLink')}
          </ClickableText>
        </Checkbox>
      </CardSection>
      <CardSection borderBottom>
        <PolicyAgreement />
      </CardSection>

      {market === CountryCode.BE && (
        <CardSection>
          <Typography
            color={theme.colors.neutral}
            lineHeight={1.2}
            fontSize={0.9}
          >
            {translate('TermsAndConfirmationCard.ItsMeWarning')}
          </Typography>
        </CardSection>
      )}

      <CardSection>
        <DownloadPdf
          isDownloading={isDownloading}
          downloadError={downloadError}
          buttonText={translate('ViewContract') as string}
          onClick={onDownloadOfferClick}
          buttonType={ButtonType.Outlined}
        />
        <Button
          disabled={
            !atLeastOneOwnerHasMoreThan25 ||
            !userData.offerIsAgreed ||
            !userData.personalLiabilityIsAgreed ||
            !userData.isPolicyAgreed
          }
          type={ButtonType.Filled}
          onClick={signContract}
          mt={1}
        >
          {isSubmitting ? <SpinnerLinear /> : translate('SignContract')}
        </Button>
      </CardSection>
    </Card>
  );
};

export default TermsAndConfirmationCard;
